import { useSelector } from 'react-redux'
import { useCallback, useEffect, useState } from 'react'
import { FlexColumn, FlexRow } from '../../atoms/layout/Flex'
import { JobSelectors } from '../../../store/job/selectors'
import CheckLabel from '../../atoms/inputs/CheckLabel'
import Accordion from '../../atoms/feedback/Accordion'
import { JobArea } from '../../../types/job'

type Props = {
  areaIds: string[]
  onChange: (areaIds: string[]) => void
}

function selectedChildArea(childAreas: JobArea[], selectedAreaIds: string[]) {
  return childAreas.some((child) => selectedAreaIds.includes(child.id))
}

export default function CompanyWorkAreaChecks({ areaIds, onChange }: Props) {
  const areas = useSelector(JobSelectors.areas)
  const [selectedAreaIds, setSelectedAreaIds] = useState<string[]>(areaIds)

  const handleSelect = useCallback(
    (areaId: string, parentAreaId?: string) => (selected: boolean) => {
      const area = areas.find((item) => item.id === areaId)
      if (selected) {
        let ids = [...selectedAreaIds, areaId]
        if (area && area.children.length > 0) {
          ids = [...ids, ...area.children.map((child) => child.id)]
        }
        if (parentAreaId) {
          const parentArea = areas.find((item) => item.id === parentAreaId)
          if (parentArea && parentArea.children.every((child) => ids.includes(child.id))) {
            ids = [...ids, parentAreaId]
          }
        }
        setSelectedAreaIds(Array.from(new Set(ids)))
      } else {
        let ids = selectedAreaIds.filter((selectedAreaId) => selectedAreaId !== areaId)
        if (area && area.children.length > 0) {
          const childAreaIds = area.children.map((item) => item.id)
          ids = ids.filter((id) => !childAreaIds.includes(id))
        }
        if (parentAreaId) {
          ids = ids.filter((id) => id !== parentAreaId)
        }
        setSelectedAreaIds(Array.from(new Set(ids)))
      }
    },
    [areas, selectedAreaIds]
  )

  useEffect(() => {
    onChange(selectedAreaIds)
  }, [onChange, selectedAreaIds])

  return (
    <FlexColumn scroll={false}>
      {areas.map((area) => (
        <FlexColumn key={area.id}>
          <Accordion
            title={
              <FlexRow>
                <CheckLabel
                  label={area.name}
                  indeterminate={
                    !selectedAreaIds.includes(area.id) && selectedChildArea(area.children, selectedAreaIds)
                  }
                  selected={selectedAreaIds.includes(area.id)}
                  onChange={handleSelect(area.id)}
                />
              </FlexRow>
            }
          >
            <FlexRow wrap ml={4}>
              {area.children.map((childArea) => (
                <CheckLabel
                  key={childArea.id}
                  label={childArea.name}
                  selected={selectedAreaIds.includes(childArea.id)}
                  onChange={handleSelect(childArea.id, area.id)}
                />
              ))}
            </FlexRow>
          </Accordion>
        </FlexColumn>
      ))}
    </FlexColumn>
  )
}
